<template>
  <view-container
    title="Clientes"
    class="view-reports-clients"
    :breadcrumbs="[{ title: 'Clientes' }]"
    header
  >
    <div class="py-6">
      <div class="row justify-content-between">
        <div class="d-flex align-items-center col-lg-3 mb-lg-0 mb-5">
          <span class="table-quantity-counter mr-5">
            Total: {{ pagination.count }}
          </span>
          <button class="btn p-0 table-refresh" @click="refresh">
            <i class="fa-solid fa-refresh" />
          </button>
        </div>
        <div class="col-xl-7 col-lg-8">
          <div class="d-flex flex-column flex-lg-row mb-3 gap-3">
            <div class="d-flex flex-fill gap-3">
              <search-filter
                placeholder="Pesquisar"
                @search="search"
                class="flex-fill"
                :showFilters="false"
                :showResultsDropdown="false"
                :newDesign="true"
              >
              </search-filter>
              <filter-button
                button-class="btn-40 btn-square btn-new-outline-primary btn-sm"
              >
                <template #default="{ close }">
                  <clients-filter
                    v-model="filters"
                    @input="close"
                    @cancel-modal="close"
                  />
                </template>
              </filter-button>
            </div>
            <bp-export-button
              v-if="$permissions.hasPermission('exportar-vendas')"
              size="lg"
              filename="clients_report"
              action="reports/getShoppers"
              :filters="filters"
              :loading="exportLoading"
              :disabled="!reports.length"
            />
          </div>
        </div>
      </div>

      <div v-if="loading" class="centering pt-6">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
      </div>
      <div v-else-if="!reports.length" class="row justify-content-center">
        <div class="col-lg-5">
          <div class="container centering text-center pt-9">
            <img
              class="d-block mb-9 mx-auto"
              src="@/assets/images/home/welcome.png"
            />
            <h5>Bem-vindo(a), você ainda não tem nenhum cliente</h5>
          </div>
        </div>
      </div>
      <div v-else>
        <div v-if="loading" class="text-center">
          <b-spinner variant="primary" />
        </div>
        <div v-else>
          <!-- Desktop area -->
          <data-table
            class="d-none d-lg-block"
            :data="reports"
            :columns="columns"
            columnsFr="0.3fr 0.8fr 1fr 0.7fr 0.3fr 0.1fr"
            :newDesign="true"
          >
            <template #default="{item}">
              <small class="d-flex align-items-center">{{ item.id }}</small>
              <div class="d-flex flex-column justify-content-center p-0">
                <strong>{{ item.name }}</strong>
                <small class="text-secondary pr-3">{{ item.email }}</small>
              </div>
              <div class="d-flex flex-column justify-content-center p-0">
                <strong v-if="item.product">{{ item.product.name }}</strong>
                <small v-if="item.plan" class="text-secondary">{{
                  item.plan.name
                }}</small>
                <small v-if="item.offer" class="text-secondary pr-2">{{
                  item.offer.name
                }}</small>
              </div>
              <span class="d-flex align-items-center p-0">
                <small>{{ dateFinished(item) }}</small>
              </span>
              <div class="p-0">
                <span
                  :class="
                    item.finished_order ? 'active-badge' : 'incomplete-badge'
                  "
                >
                  <span class="fs-12">{{
                    item.finished_order ? "Realizada" : "Incompleta"
                  }}</span>
                </span>
              </div>
              <div class="d-flex justify-content-end align-items-center">
                <actions-dropdown
                  :left="false"
                  :right="true"
                  :actions="actions"
                  @detail="detail(item)"
                />
              </div>
            </template>
          </data-table>
          <!-- End of desktop -->
          <!-- Mobile area -->
          <data-table
            class="d-lg-none"
            :data="reports"
            :columns="['', '']"
            :newDesign="true"
          >
            <template #default="{item}">
              <div>
                <div class="mb-3">
                  <small
                    ><strong class="mr-2">ID: </strong>
                    {{ item.id }}
                  </small>
                </div>
                <div class="mb-3">
                  <small
                    ><strong class="mr-2">Cliente:</strong>
                    {{ item.name }}
                  </small>
                </div>
                <div class="mb-3">
                  <small
                    ><strong class="mr-2">Email:</strong>
                    {{ item.email }}
                  </small>
                </div>
                <div class="mb-3">
                  <small v-if="item.product"
                    ><strong class="mr-2">Produto:</strong>
                    {{ item.product.name }}
                  </small>
                </div>
                <div class="mb-3">
                  <small v-if="item.offer"
                    ><strong class="mr-2">Oferta:</strong>
                    {{ item.offer.name }}
                  </small>
                </div>
                <div class="mb-3">
                  <small v-if="item.plan"
                    ><strong class="mr-2">Plano:</strong>
                    {{ item.plan.name }}
                  </small>
                </div>
                <div class="mb-3">
                  <small
                    ><strong class="mr-2">Data: </strong>
                    <span>
                      {{ dateFinished(item) }}
                    </span>
                  </small>
                </div>
                <div class="mb-3">
                  <small
                    ><strong class="mr-2">Status: </strong>
                    <span
                      :class="
                        item.finished_order
                          ? 'active-badge'
                          : 'incomplete-badge'
                      "
                    >
                      <span>{{
                        item.finished_order ? "Realizada" : "Incompleta"
                      }}</span>
                    </span>
                  </small>
                </div>
              </div>
              <div class="d-flex align-items-center justify-content-end">
                <actions-dropdown
                  :left="false"
                  :right="true"
                  :actions="actions"
                  @detail="detail(item)"
                />
              </div>
            </template>
          </data-table>
          <!-- End of mobile -->
        </div>
      </div>

      <div v-if="reports.length" class="d-flex justify-content-center mt-5">
        <paginator :pagination="pagination" @on-update="onPaginationUpdate" />
      </div>

      <responsive-modal v-model="detailDialog" size="lg">
        <client-details :client="detailedItem" @close="detailDialog = false" />
      </responsive-modal>
    </div>
  </view-container>
</template>

<script>
import ClientDetails from "@/components/reports/ClientDetails";
import ClientsFilter from "@/components/reports/ClientsFilter";
import { mapState } from "vuex";
import ViewContainer from "../../components/ViewContainer";
import { toHumanDate } from "@/functions/helpers";

export default {
  components: { ClientsFilter, ClientDetails, ViewContainer },
  data() {
    return {
      loading: false,
      detailedItem: null,
      detailDialog: false,
      exportLoading: false,
      filters: {
        finished_order: "",
        product_id: "",
        offer_id: "",
        plan_id: "",
        sck: ""
      },
      params: {
        search: "",
        limit: 20,
        offset: 0
      },
      actions: [
        {
          text: "Detalhes",
          class: "",
          icon: "fa-solid fa-circle-info",
          confirmation: { enable: false },
          event: "detail"
        }
      ],
      columns: [
        "ID",
        "Comprador",
        "Produto",
        "Data da compra",
        "Status de compra",
        ""
      ]
    };
  },
  mounted() {
    this.dispatch();
    this.$store.dispatch("product/get", { limit: 1000 }).then();
  },
  computed: {
    ...mapState("reports", ["reports", "pagination"]),
    ...mapState("product", ["products"])
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.dispatch();
      }
    }
  },
  methods: {
    refresh() {
      this.params.search = "";
      this.dispatch();
    },
    search(text) {
      this.params.search = text;
      this.dispatch();
    },
    catchHandler(error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.details
      ) {
        this.$message.error(error.response.data.details);
      }
    },
    onPaginationUpdate(offset) {
      this.params.offset = offset;
      this.dispatch();
    },
    dispatch() {
      const params = {
        ...this.filters,
        ...this.params
      };
      this.loading = true;
      this.$store
        .dispatch("reports/getShoppers", params)
        .then()
        .catch(this.catchHandler)
        .finally(() => {
          this.loading = false;
        });
    },
    detail(item) {
      this.detailDialog = true;
      this.detailedItem = item;
    },
    dateFinished(item) {
      if (!item.finished_order) return;
      return toHumanDate(item.finished_order);
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.view-reports-clients {
  .btn {
    &:not(.btn-sm) {
      min-height: 28px;
    }
    @media screen and (max-width: 500px) {
      padding-left: 5px !important;
      padding-right: 5px !important;
    }
  }
}
</style>
